import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState, useRef, useCallback, useLayoutEffect, useMemo } from "react";
import {
  FaCalendarAlt,
  FaTachometerAlt,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaGasPump,
  FaCog,
  FaComments,
} from "react-icons/fa";
import { fetchVehicleData } from "../../../api/fetchVehicleData";
import "./CustomCarousel.css";
import NavigationDots from "./NavigationDots";
import VehicleDialog from "./VehicleDialog";
import CatalogTrimDialog from "./CatalogTrimDialog";
import { trackChatEvent } from '../../../utils/analytics';
import { useWebSocketContext } from '../../../context/Theme';
import { createTranslator, getBrowserLanguage } from "../../../utils/i18n";

const DESKTOP_CARD_W = 290;
const CARD_GAP = 12;
const isCat = (i) => i?.vehicleid?.startsWith('catalog_');
const tCode = (i) => i?.vehicleid?.replace(/^catalog_/, '') ?? '';

const badgeColor = (b) => {
  if (!b) return '#64748b';
  const l = b.toLowerCase();
  if (l.includes('nuovo') || l.includes('nuova')) return '#10b981';
  if (l.includes('km 0') || l.includes('km0')) return '#3b82f6';
  if (l.includes('usato')) return '#f59e0b';
  if (l.includes('pronta consegna')) return '#8b5cf6';
  if (l.includes('aziendale')) return '#06b6d4';
  return '#64748b';
};

const Pill = ({ icon, children }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    bgcolor: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '7px', px: 0.75, py: 0.3,
    fontSize: '0.72rem', color: '#64748b', fontWeight: 500,
    lineHeight: 1, whiteSpace: 'nowrap',
  }}>
    {icon}{children}
  </Box>
);

const Btn = motion.create(Button);
const MCard = motion.create(Card);
const MIconBtn = motion.create(IconButton);

const CustomCarousel = ({ items, whishlist, onWishlistUpdate, onSendMessage }) => {
  const containerRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(0);
  const [open, setOpen] = useState(false);
  const [vData, setVData] = useState(null);
  const [cpv, setCpv] = useState(1);
  const [cardW, setCardW] = useState(DESKTOP_CARD_W);
  const [cw, setCw] = useState(DESKTOP_CARD_W);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [dLoading, setDLoading] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [catCar, setCatCar] = useState(null);
  const [ts, setTs] = useState(null);
  const [te, setTe] = useState(null);
  const { handleSend, isLoading } = useWebSocketContext() || {};
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  const numberLocale = language === "en" ? "en-US" : language === "es" ? "es-ES" : "it-IT";
  const fmt = useCallback(
    (value) =>
      parseFloat(value).toLocaleString(numberLocale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [numberLocale]
  );

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const pw = el.parentElement?.offsetWidth ?? 320;
    const mobile = window.innerWidth < 600;
    setIsMobile(mobile);

    if (mobile) {
      const w = Math.min(pw - 16, 340);
      setCardW(w);
      setCw(w);
      setCpv(1);
    } else {
      const pad = 48;
      const avail = pw - 2 * pad;
      if (avail < DESKTOP_CARD_W) {
        setCardW(DESKTOP_CARD_W);
        setCw(DESKTOP_CARD_W);
        setCpv(1);
      } else {
        const n = Math.min(Math.floor((avail + CARD_GAP) / (DESKTOP_CARD_W + CARD_GAP)), items.length);
        setCpv(n);
        setCw((DESKTOP_CARD_W + CARD_GAP) * n - CARD_GAP);
        setCardW(DESKTOP_CARD_W);
      }
    }
  }, [items.length]);

  const refCb = useCallback((node) => {
    if (!node) return;
    containerRef.current = node;
    const obs = new ResizeObserver(measure);
    obs.observe(node.parentElement);
    measure();
  }, [measure]);

  // Re-measure on mount
  useLayoutEffect(measure, [measure]);

  if (!items.length) return null;

  const imgH = isMobile ? 155 : 175;

  const prev = () => { trackChatEvent.carouselNav('prev'); setDir(-1); setIdx(p => { const n = p - cpv; return n < 0 ? Math.max(0, items.length - cpv) : n; }); };
  const next = () => { trackChatEvent.carouselNav('next'); setDir(1); setIdx(p => { const n = p + cpv; return n >= items.length ? 0 : n; }); };
  const dotClick = (i) => { trackChatEvent.carouselNav(`dot-${i}`); setDir(i > idx ? 1 : -1); setIdx(i); };

  const openPopup = async (i, e) => {
    e?.preventDefault(); e?.stopPropagation();
    if (dLoading) return;
    trackChatEvent.viewVehicleDetails(items[i].vehicleid, items[i].title);
    setOpen(true); setDLoading(true); setVData(null);
    try { setVData(await fetchVehicleData(items[i].vehicleid)); } catch {} finally { setDLoading(false); }
  };

  const bookVisit = (it) => {
    trackChatEvent.bookVisit(it.vehicleid, it.title);
    const m = `Vorrei prenotare una visita per: ${it.title} (ID: ${it.vehicleid})`;
    handleSend ? handleSend(m, null, null, { type: 'chat_message', sender: 'user' }) : onSendMessage?.(m, null, null, { type: 'chat_message', sender: 'user' });
  };
  const openPage = (it) => {
    trackChatEvent.openVehiclePage(it.vehicleid, it.title);
    window.open(`${it.url}${it.url?.includes('?') ? '&' : '?'}utm_source=carousel_mia_${it.vehicleid}&utm_medium=vehicle_card_mia&utm_campaign=vehicle_listing_mia`, '_blank');
  };
  const openCatDialog = (it) => { setCatCar(it); setCatOpen(true); };
  const configReq = (it) => {
    const m = `Vorrei configurare: ${it.title}. Codice allestimento: ${tCode(it)}`;
    trackChatEvent.sendMessage(m.length);
    handleSend ? handleSend(m, null, null, { type: 'chat_message', sender: 'user' }) : onSendMessage?.(m);
  };

  const prenotaVisita = (it) => {
    const m = `Vorrei prenotare una visita per: ${it.title} (ID veicolo: ${it.vehicleid})`;
    trackChatEvent.sendMessage(m.length);
    handleSend ? handleSend(m, null, null, { type: 'chat_message', sender: 'user' }) : onSendMessage?.(m);
  };

  const richiediPreventivoStock = (it) => {
    const m = `Sono interessato a questo veicolo, vorrei ricevere un preventivo: ${it.title} (ID veicolo: ${it.vehicleid})`;
    trackChatEvent.sendMessage(m.length);
    handleSend ? handleSend(m, null, null, { type: 'chat_message', sender: 'user' }) : onSendMessage?.(m);
  };

  const richiediPreventivoConfig = (it) => {
    const m = `Vorrei ricevere un preventivo per questo allestimento: ${it.title} (Codice: ${tCode(it)})`;
    trackChatEvent.sendMessage(m.length);
    handleSend ? handleSend(m, null, null, { type: 'chat_message', sender: 'user' }) : onSendMessage?.(m);
  };

  return (
    <>
      <Box ref={refCb} className="carousel-container" sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        position: 'relative', width: '100%',
        touchAction: 'pan-y pinch-zoom',
        overflow: 'visible',
      }}>
        <Box
          sx={{ width: cw, position: 'relative', mx: 'auto' }}
          onTouchStart={(e) => { setTe(null); setTs(e.targetTouches[0].clientX); }}
          onTouchMove={(e) => setTe(e.targetTouches[0].clientX)}
          onTouchEnd={() => { if (!ts || !te) return; const d = ts - te; if (Math.abs(d) > 50) d > 0 ? next() : prev(); }}
        >
          <AnimatePresence initial={false} custom={dir} mode="popLayout">
            <motion.div
              key={idx} custom={dir}
              initial="in" animate="active" exit="out"
              transition={{ duration: isMobile ? 0.25 : 0.4, ease: [0.4, 0, 0.2, 1] }}
              variants={{
                in: (d) => ({ x: d > 0 ? cw : -cw, opacity: 0 }),
                active: { x: 0, opacity: 1 },
                out: (d) => ({ x: d > 0 ? -cw : cw, opacity: 0 }),
              }}
              style={{ display: 'flex', gap: CARD_GAP, width: cw }}
            >
              {items.slice(idx, idx + cpv).map((item, i) => {
                const op = parseFloat(item.description4 || '0');
                const np = parseFloat(item.description2 || '0');
                const disc = op > np && op > 0;
                const isNew = (item.badge || '').toLowerCase() === 'nuovo';
                const showDisc = disc && isNew;
                const discPct = showDisc ? Math.round((1 - np / op) * 100) : 0;
                const cat = isCat(item);

                return (
                  <MCard
                    key={`${item.vehicleid}-${i}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.05 }}
                    sx={{
                      width: cardW, flexShrink: 0,
                      borderRadius: isMobile ? '14px' : '16px',
                      overflow: 'hidden',
                      display: 'flex', flexDirection: 'column',
                      bgcolor: '#fff', position: 'relative',
                      boxShadow: '0 1px 8px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)',
                      borderTop: cat ? '3px solid #6366f1' : 'none',
                      '&:hover': !isMobile ? { boxShadow: '0 6px 24px rgba(0,0,0,0.12)' } : {},
                      transition: 'box-shadow 0.2s',
                    }}
                  >
                    {/* ── IMAGE ── */}
                    <Box
                      sx={{ position: 'relative', height: imgH, overflow: 'hidden', cursor: 'pointer', flexShrink: 0 }}
                      onClick={(e) => openPopup(idx + i, e)}
                    >
                      <CardMedia
                        component="img"
                        height={String(imgH)}
                        src={item.image}
                        sx={{ objectFit: 'cover', transition: 'transform 0.35s', '&:hover': !isMobile ? { transform: 'scale(1.04)' } : {} }}
                        onError={(e) => {
                          if (!e?.target) return;
                          e.target.src = localStorage.getItem('logo') || `${process.env.REACT_APP_DOMAIN}/media/company_logos/3_ms-icon-144x144.png`;
                          e.target.style.objectFit = 'contain';
                          e.target.style.padding = '16px';
                          e.target.style.backgroundColor = '#f1f5f9';
                        }}
                      />
                      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.45) 100%)', pointerEvents: 'none' }} />

                      {item.badge && (
                        <Box sx={{ position: 'absolute', top: 8, left: 8, bgcolor: badgeColor(item.badge), color: '#fff', px: 1, py: 0.3, borderRadius: '6px', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.3px', boxShadow: '0 2px 6px rgba(0,0,0,0.18)' }}>
                          {item.badge}
                        </Box>
                      )}
                      {showDisc && <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#ef4444', color: '#fff', px: 1, py: 0.3, borderRadius: '6px', fontWeight: 800, fontSize: '0.72rem', boxShadow: '0 2px 6px rgba(0,0,0,0.18)' }}>-{discPct}%</Box>}
                      {cat && !showDisc && <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(99,102,241,0.88)', backdropFilter: 'blur(3px)', color: '#fff', px: 1, py: 0.3, borderRadius: '6px', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t("orderable")}</Box>}

                      {!item.available && (
                        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', bgcolor: 'rgba(239,68,68,0.85)', px: 1.5, py: 0.5, borderRadius: '8px' }}>{t("unavailable")}</Typography>
                        </Box>
                      )}

                      {np > 0 && (
                        <Box onClick={(e) => e.stopPropagation()} sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                          <Box sx={{ bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', px: 1, py: 0.4, borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            {showDisc && <Typography sx={{ textDecoration: 'line-through', color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', fontWeight: 500, lineHeight: 1 }}>€{fmt(op)}</Typography>}
                            <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: isMobile ? '0.88rem' : '0.95rem', lineHeight: 1.1 }}>{cat ? t("from") : ''}€{fmt(np)}</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* ── CONTENT ── */}
                    <CardContent sx={{
                      flex: 1, display: 'flex', flexDirection: 'column',
                      p: isMobile ? '8px 10px 10px' : '12px 14px 14px',
                      gap: isMobile ? '5px' : '7px',
                      '&:last-child': { pb: isMobile ? '10px' : '14px' },
                    }}>
                      <Tooltip title={item.title} arrow enterDelay={500}>
                        <Typography sx={{
                          fontWeight: 700, fontSize: isMobile ? '0.82rem' : '0.92rem',
                          lineHeight: 1.25, color: '#111827',
                          display: '-webkit-box', WebkitLineClamp: isMobile ? 1 : 2,
                          WebkitBoxOrient: 'vertical', overflow: 'hidden',
                          wordBreak: 'break-word',
                        }}>
                          {item.title}
                        </Typography>
                      </Tooltip>

                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {cat ? (
                          <>
                            {item.fuel_type && <Chip label={item.fuel_type} size="small" sx={{ height: 18, fontSize: '0.63rem', bgcolor: '#f0fdf4', color: '#166534', fontWeight: 600, border: '1px solid #bbf7d0' }} />}
                            {item.power_hp && <Chip label={`${item.power_hp} CV`} size="small" sx={{ height: 18, fontSize: '0.63rem', bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, border: '1px solid #bfdbfe' }} />}
                            {item.transmission && <Chip label={item.transmission} size="small" sx={{ height: 18, fontSize: '0.63rem', bgcolor: '#faf5ff', color: '#6b21a8', fontWeight: 600, border: '1px solid #e9d5ff' }} />}
                          </>
                        ) : (
                          <>
                            {(() => { const b = (item.badge || '').toLowerCase(); return item.description1 && b !== 'nuovo' && !b.includes('km') ? <Pill icon={<FaTachometerAlt size={9} />}>{fmt(item.description1)} km</Pill> : null; })()}
                            {item.description3 && <Pill icon={<FaCalendarAlt size={9} />}>{item.description3}</Pill>}
                            {(item.fuel || item.description5) && <Pill icon={<FaGasPump size={9} />}>{item.fuel || item.description5}</Pill>}
                          </>
                        )}
                      </Box>

                      {/* Desktop extras */}
                      {!isMobile && item.promotion && item.promotionDetails && (
                        <Box sx={{ p: 1, bgcolor: '#f5f3ff', borderRadius: '8px', border: '1px solid #ddd6fe', maxHeight: 60, overflow: 'auto' }}>
                          <Typography sx={{ color: '#7c3aed', fontWeight: 700, fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.25 }}>
                            <Box component="span" sx={{ width: 13, height: 13, borderRadius: '50%', bgcolor: '#8b5cf6', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.58rem', fontWeight: 800, flexShrink: 0 }}>%</Box>
                            {item.promotion}
                          </Typography>
                          <Typography sx={{ color: '#4b5563', fontSize: '0.7rem', lineHeight: 1.3 }}>{item.promotionDetails}</Typography>
                        </Box>
                      )}

                      {!isMobile && !cat && np > 0 && (
                        <Box sx={{ p: '8px 10px', bgcolor: showDisc ? '#fff5f5' : '#f8fafc', borderRadius: '8px', border: `1px solid ${showDisc ? '#fed7d7' : '#e2e8f0'}` }}>
                          {showDisc && <Typography sx={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '0.76rem', fontWeight: 500 }}>€{fmt(op)}</Typography>}
                          <Typography sx={{ fontWeight: 800, color: showDisc ? '#dc2626' : '#111827', fontSize: '1.1rem', lineHeight: 1 }}>€{fmt(np)}</Typography>
                          {showDisc && <Typography sx={{ color: '#059669', fontSize: '0.7rem', fontWeight: 600, mt: 0.3, display: 'flex', alignItems: 'center', gap: 0.4 }}><FaCheckCircle size={10} />{t("saveAmount", { amount: fmt(op - np) })}</Typography>}
                        </Box>
                      )}

                      {isMobile && showDisc && (
                        <Typography sx={{ fontSize: '0.68rem', color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <FaCheckCircle size={9} />{t("saveAmount", { amount: fmt(op - np) })}
                        </Typography>
                      )}

                      <Box sx={{ flex: 1 }} />

                      {/* ── CTAs ── */}
                      {cat ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <Btn size="small" onClick={(e) => { e.stopPropagation(); richiediPreventivoConfig(item); }} whileTap={{ scale: 0.97 }}
                            disabled={isLoading}
                            startIcon={<FaComments style={{ fontSize: isMobile ? '0.72rem' : '0.8rem' }} />}
                            sx={{
                              fontSize: isMobile ? '0.75rem' : '0.8rem',
                              borderRadius: isMobile ? '9px' : '10px',
                              bgcolor: '#6366f1', color: '#fff',
                              py: isMobile ? 0.65 : 0.85, textTransform: 'none', fontWeight: 700,
                              boxShadow: '0 2px 8px rgba(99,102,241,0.28)',
                              '&:hover': { bgcolor: '#4f46e5' },
                              '&.Mui-disabled': { opacity: 0.5 },
                            }}>
                            {t("requestQuote")}
                          </Btn>
                          <Btn size="small" onClick={(e) => { e.stopPropagation(); openCatDialog(item); }} whileTap={{ scale: 0.97 }}
                            startIcon={<FaCog style={{ fontSize: isMobile ? '0.68rem' : '0.76rem' }} />}
                            sx={{
                              fontSize: isMobile ? '0.72rem' : '0.77rem',
                              borderRadius: isMobile ? '9px' : '10px',
                              bgcolor: '#f0f0ff', color: '#4f46e5',
                              py: isMobile ? 0.55 : 0.7, textTransform: 'none', fontWeight: 600,
                              border: '1.5px solid #c7d2fe',
                              '&:hover': { bgcolor: '#e0e7ff' },
                            }}>
                            {t("seeOptionsAndColors")}
                          </Btn>
                        </Box>
                      ) : item.available ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <Btn size="small" onClick={() => prenotaVisita(item)} whileTap={{ scale: 0.97 }}
                            disabled={isLoading}
                            startIcon={<FaCalendarAlt style={{ fontSize: isMobile ? '0.72rem' : '0.8rem' }} />}
                            sx={{
                              fontSize: isMobile ? '0.75rem' : '0.8rem',
                              borderRadius: isMobile ? '9px' : '10px',
                              bgcolor: '#10b981', color: '#fff',
                              py: isMobile ? 0.65 : 0.85, textTransform: 'none', fontWeight: 700,
                              boxShadow: '0 2px 8px rgba(16,185,129,0.25)',
                              '&:hover': { bgcolor: '#059669' },
                              '&.Mui-disabled': { opacity: 0.5 },
                            }}>
                            {t("bookVisit")}
                          </Btn>
                          <Btn size="small" onClick={() => richiediPreventivoStock(item)} whileTap={{ scale: 0.97 }}
                            disabled={isLoading}
                            startIcon={<FaComments style={{ fontSize: isMobile ? '0.68rem' : '0.74rem' }} />}
                            sx={{
                              fontSize: isMobile ? '0.72rem' : '0.77rem',
                              borderRadius: isMobile ? '9px' : '10px',
                              bgcolor: '#f8fafc', color: '#475569',
                              py: isMobile ? 0.55 : 0.7, textTransform: 'none', fontWeight: 600,
                              border: '1.5px solid #e2e8f0',
                              '&:hover': { bgcolor: '#f1f5f9' },
                              '&.Mui-disabled': { opacity: 0.5 },
                            }}>
                            {t("requestQuote")}
                          </Btn>
                          {(item.url || item.promotion_document__url) && (
                            <Box sx={{ display: 'flex', gap: '5px' }}>
                              {item.url && (
                                <Btn size="small" onClick={() => openPage(item)} whileTap={{ scale: 0.97 }}
                                  sx={{ flex: 1, fontSize: isMobile ? '0.68rem' : '0.71rem', borderRadius: isMobile ? '8px' : '9px', bgcolor: '#f8fafc', color: '#64748b', py: isMobile ? 0.4 : 0.5, textTransform: 'none', fontWeight: 600, border: '1.5px solid #e2e8f0', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                  {t("technicalSheet")}
                                </Btn>
                              )}
                              {item.promotion_document__url && (
                                <Btn size="small" component="a" href={item.promotion_document__url} target="_blank" rel="noopener noreferrer"
                                  whileTap={{ scale: 0.97 }} onClick={(e) => e.stopPropagation()}
                                  sx={{ flex: 1, fontSize: isMobile ? '0.68rem' : '0.71rem', borderRadius: isMobile ? '8px' : '9px', bgcolor: '#f5f3ff', color: '#6d28d9', py: isMobile ? 0.4 : 0.5, textTransform: 'none', fontWeight: 600, border: '1.5px solid #ddd6fe', '&:hover': { bgcolor: '#ede9fe' } }}>
                                  {t("specialOffer")}
                                </Btn>
                              )}
                            </Box>
                          )}
                        </Box>
                      ) : null}
                    </CardContent>
                  </MCard>
                );
              })}
            </motion.div>
          </AnimatePresence>

          {/* Arrows (desktop only – side arrows) */}
          {!isMobile && items.length > cpv && ['left', 'right'].map((side) => (
            <MIconBtn key={side} onClick={side === 'left' ? prev : next}
              whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              sx={{
                position: 'absolute', [side]: -42,
                top: '50%', transform: 'translateY(-50%)', zIndex: 2,
                bgcolor: 'rgba(17,24,39,0.5)', backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255,255,255,0.12)',
                '&:hover': { bgcolor: 'rgba(17,24,39,0.75)' },
                borderRadius: '50%', width: 36, height: 36,
              }}>
              <motion.svg focusable="false" viewBox="-4 0 24 24" width="18" height="18" fill="#fff"
                style={{ transform: side === 'left' ? 'rotate(180deg)' : 'none' }}>
                <path d="M6.23 20.23 8 22l10-10L8 2 6.23 3.77 14.46 12z" />
              </motion.svg>
            </MIconBtn>
          ))}
        </Box>

        {items.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 0.5 : 0, mt: isMobile ? 0.25 : 0 }}>
            {isMobile && (
              <MIconBtn onClick={prev} whileTap={{ scale: 0.88 }} disabled={items.length <= 1}
                sx={{ width: 32, height: 32, bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '50%', color: '#64748b', '&:hover': { bgcolor: '#e2e8f0' }, '&.Mui-disabled': { opacity: 0.3 } }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M17.77 3.77 16 2 6 12l10 10 1.77-1.77L9.54 12z" /></svg>
              </MIconBtn>
            )}
            <NavigationDots count={Math.ceil(items.length / cpv)} activeIndex={Math.floor(idx / cpv)} onDotClick={dotClick} />
            {isMobile && (
              <MIconBtn onClick={next} whileTap={{ scale: 0.88 }} disabled={items.length <= 1}
                sx={{ width: 32, height: 32, bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '50%', color: '#64748b', '&:hover': { bgcolor: '#e2e8f0' }, '&.Mui-disabled': { opacity: 0.3 } }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M6.23 20.23 8 22l10-10L8 2 6.23 3.77 14.46 12z" /></svg>
              </MIconBtn>
            )}
          </Box>
        )}
      </Box>

      <VehicleDialog open={open} handleClose={() => setOpen(false)} vehicleData={vData} isLoading={dLoading} />
      <CatalogTrimDialog open={catOpen} onClose={() => setCatOpen(false)} car={catCar}
        onAskAssistant={(m) => { if (handleSend) handleSend(m, null, null, { type: 'chat_message', sender: 'user' }); setCatOpen(false); }} />
    </>
  );
};

export default CustomCarousel;
